import { ConfirmDestructive } from '@ccp/ui';
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  Navigate,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { areasFor } from '../app/shell/areas.js';
import { WorkspaceShell } from '../app/shell/workspace-shell.js';
import { CommandPalette } from '../features/command/command-palette.js';
import { CommandRegistryProvider, type PaletteAction } from '../features/command/registry.js';
import { SupportGrantBanner } from '../features/settings/grants/components/support-grant-banner.js';
import { currentOrRestoredStatus, signOut } from '../session/session.js';
import { useSessionStatus } from '../session/session.store.js';
import { SessionProvider, useConnectionStatus } from '../session/session-provider.js';
import { useCurrentUser } from '../session/use-current-user.js';

export interface RouterContext {
  readonly queryClient: QueryClient;
}

/** The routes a person reaches without a session. Everything else is behind the gate. */
const publicPrefix = '/auth';

/**
 * The shell every route renders inside, and the gate in front of all of it.
 *
 * The gate is one `beforeLoad` rather than one per route, because a route added without it would be
 * a route with no gate — and the mistake would look exactly like a route that was always public.
 * It is also only half the story: the API authorises every call behind it, which is why a gate that
 * guessed wrong would cost a redirect and not a disclosure.
 *
 * The support-grant banner is mounted here rather than on a page because the thing it says —
 * somebody from the vendor can see what you are doing — is true wherever the person happens to be
 * looking (operator access §1.3).
 */
export const Route = createRootRouteWithContext<RouterContext>()({
  async beforeLoad({ location }) {
    if (location.pathname.startsWith(publicPrefix)) {
      return;
    }
    if ((await currentOrRestoredStatus()) !== 'authenticated') {
      // Where they were going, so signing in takes them there rather than to the front door.
      throw redirect({ to: '/auth/login', search: { redirect: location.href } });
    }
  },
  component: RootLayout,
  notFoundComponent: NotFound,
});

function RootLayout() {
  const status = useSessionStatus();
  // The session's machinery is mounted only when there is a session: a sign-in page has no socket
  // to open, no agent to be, and no `/v1/me` to ask for.
  return status === 'authenticated' ? (
    <SessionProvider>
      {/* The registry wraps the shell, because what the palette offers comes from the screens
          inside it: the provider has to be above both. */}
      <CommandRegistryProvider>
        <SignedInShell />
      </CommandRegistryProvider>
    </SessionProvider>
  ) : (
    <WorkspaceShell areas={[]} connection="idle">
      <SignedOutOutlet />
    </WorkspaceShell>
  );
}

/**
 * What a person without a session may see: the public pages, and nothing else.
 *
 * The gate in `beforeLoad` runs when a route loads, not when a session ends under a page that is
 * already open. Signing out on the agent screen — or a refresh that fails mid-shift — took the
 * session's providers away and left the page rendering without them, which crashed to the
 * router's error screen ("must be rendered inside a SessionProvider"). Found when sign-out gained
 * its confirmation and a test signed out from a page for the first time (M5.5-01).
 */
function SignedOutOutlet() {
  const location = useLocation();
  if (location.pathname.startsWith(publicPrefix)) {
    return <Outlet />;
  }
  return <Navigate to="/auth/login" search={{ redirect: location.href }} replace />;
}

function SignedInShell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const connection = useConnectionStatus();
  const { data: me } = useCurrentUser();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);

  const areas = me === undefined ? [] : areasFor(me);
  // The shell's own actions: where a person may go, and leaving. Everything that acts on what is on
  // screen is published by the screen itself through the registry.
  const actions: PaletteAction[] = [
    ...areas.map((area) => ({
      id: `goTo.${area.id}`,
      group: 'goTo' as const,
      label: t(area.labelKey),
      run: () => void navigate({ to: area.path }),
    })),
    {
      id: 'session.signOut',
      group: 'session' as const,
      label: t('nav.signOut'),
      run: () => setConfirmingSignOut(true),
    },
  ];

  return (
    <WorkspaceShell
      areas={areas}
      connection={connection}
      userName={me?.displayName}
      userEmail={me?.email}
      onSignOut={() => setConfirmingSignOut(true)}
      onOpenCommandPalette={() => setPaletteOpen(true)}
      showDensity
    >
      <SupportGrantBanner />
      <Outlet />
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} actions={actions} />
      {/* One question, whichever way sign-out was asked for — the account menu or the palette.
          Signing out mid-shift takes an agent out of the calls and chats they would be offered,
          and drops anything unsaved on screen, so it is confirmed as a destructive action. It is
          not irreversible, so nothing has to be typed (M5.5-01). */}
      <ConfirmDestructive
        open={confirmingSignOut}
        onOpenChange={setConfirmingSignOut}
        title={t('signOutConfirm.title')}
        description={t('signOutConfirm.description')}
        confirmLabel={t('signOutConfirm.confirm')}
        cancelLabel={t('signOutConfirm.cancel')}
        onConfirm={() => {
          setConfirmingSignOut(false);
          void signOut();
        }}
      />
    </WorkspaceShell>
  );
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <div className="p-(--space-xl) text-(length:--font-size-body) text-(--color-text-secondary)">
      {t('state.notFound')}
    </div>
  );
}
